import type { TFunction } from "i18next"

import type { AutomationOptions, Summary } from "../types/automation.types"
import {
  type AutomationFormValues,
  type ScheduleWarning,
  formValuesToPayload
} from "./form-values"
import { localizeDuration, localizeSummary } from "./localized-summary"

// The plain-language rule for the values the form would submit — read from the
// payload, so it describes exactly what gets saved.
export const summarizeFormValues = (
  t: TFunction,
  values: AutomationFormValues,
  options: AutomationOptions
): Summary => {
  const state = options.lead_states.find(
    (item) => item.id === values.lead_status
  )
  const sender = options.senders.find((item) => item.id === values.sender_user)
  const payload = formValuesToPayload(values)
  return localizeSummary(t, {
    category: payload.category,
    state_label: state?.label ?? t("settingsAutomation.summary.aState"),
    sender_email: sender?.email ?? t("settingsAutomation.summary.theSender"),
    delay_value: payload.initial_delay_value,
    delay_unit: payload.initial_delay_unit,
    cadence_value: payload.cadence_value,
    cadence_unit: payload.cadence_unit,
    send_count: payload.send_count
  })
}

export interface JourneyStep {
  key: "trigger" | "wait" | "send"
  title: string
  text: string
}

// The three-step journey shown beside the rule. Wait and repeat texts come
// from the same summary, so they read exactly as the sentence does.
export const journeySteps = (
  t: TFunction,
  values: AutomationFormValues,
  options: AutomationOptions,
  summary: Summary = summarizeFormValues(t, values, options)
): JourneyStep[] => {
  const state = options.lead_states.find(
    (item) => item.id === values.lead_status
  )
  const step = (key: JourneyStep["key"]) =>
    summary.steps.find((item) => item.key === key)
  const trigger: JourneyStep =
    values.category === "NURTURING"
      ? {
          key: "trigger",
          title: t("settingsAutomation.review.journey.audience"),
          text: step("trigger")?.text ?? ""
        }
      : {
          key: "trigger",
          title: t("settingsAutomation.review.journey.enters"),
          text: state?.label ?? t("settingsAutomation.review.journey.noState")
        }
  return [
    trigger,
    {
      key: "wait",
      title: step("wait")?.title ?? "",
      text: step("wait")?.text ?? ""
    },
    {
      key: "send",
      title: step("send")?.title ?? "",
      text:
        formValuesToPayload(values).send_count === 1
          ? t("settingsAutomation.review.journey.singleSend")
          : (step("send")?.text ?? "")
    }
  ]
}

const SCHEDULE_WARNING_KEYS: Record<ScheduleWarning, string> = {
  restart: "settingsAutomation.builder.trigger.scheduleRestart",
  restartOnResume: "settingsAutomation.builder.trigger.scheduleRestartOnResume",
  cancel: "settingsAutomation.builder.trigger.scheduleCancel",
  startNurturing: "settingsAutomation.builder.trigger.scheduleStartNurturing"
}

/** What saving does to the Nurturing sends, naming when a new schedule's first emails go out. */
export const scheduleWarningText = (
  t: TFunction,
  warning: ScheduleWarning,
  values: AutomationFormValues
) => {
  const payload = formValuesToPayload(values)
  return t(SCHEDULE_WARNING_KEYS[warning], {
    delay: localizeDuration(
      t,
      payload.initial_delay_value,
      payload.initial_delay_unit
    )
  })
}
